import React from 'react';
import { useReveal } from './Reveal';
import { useLang } from '../context/LanguageContext';
import ChartNote from './ChartNote';

// Blue/slate palette only (compliance with design rules – no dark colourful gradients).
const PALETTE = ['#94a3b8', '#2563eb', '#1e3a8a'];

// Grouped vertical bar chart for scientific comparisons.
// series: [{ label, values:[], color? }]  categories: ['1 Tag', ...]
const ScienceChart = ({ title, categories = [], series = [], unit, methodology, summary, tableLabel = 'Datentabelle' }) => {
  const { ref, visible } = useReveal({ threshold: 0.25 });
  const { lang } = useLang();
  const max = Math.max(...series.flatMap((s) => s.values), 1);
  const fmt = (v) => (typeof v === 'number' ? v.toLocaleString(lang === 'de' ? 'de-DE' : 'en-US') : v);

  return (
    <figure ref={ref} className="rounded-xl border border-slate-200 bg-white p-6 lg:p-7">
      {title && <figcaption className="text-[15px] font-bold text-slate-900 mb-1">{title}</figcaption>}
      {unit && <p className="text-[12px] text-slate-500 mb-5">{unit}</p>}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6">
        {series.map((s, i) => (
          <span key={i} className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-600">
            <span className="w-3 h-3 rounded-sm" style={{ background: s.color || PALETTE[i % PALETTE.length] }} />
            {s.label}
          </span>
        ))}
      </div>

      {/* Plot */}
      <div className="flex items-end justify-around gap-4 h-[240px] border-b border-slate-200 pb-0">
        {categories.map((cat, ci) => (
          <div key={ci} className="flex-1 flex flex-col items-center justify-end h-full">
            <div className="flex items-end justify-center gap-1.5 w-full h-full">
              {series.map((s, si) => {
                const v = s.values[ci];
                const h = visible ? (v / max) * 100 : 0;
                return (
                  <div key={si} className="flex-1 max-w-[46px] h-full flex items-end" title={`${s.label}: ${fmt(v)}`}>
                    <div
                      className="relative w-full rounded-t-sm"
                      style={{
                        height: `${h}%`,
                        background: s.color || PALETTE[si % PALETTE.length],
                        transition: 'height 0.9s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      <span
                        className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10.5px] font-bold text-slate-700 whitespace-nowrap"
                        style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 0.3s' }}
                      >
                        {fmt(v)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <span className="mt-2 text-[12px] font-semibold text-slate-500 text-center">{cat}</span>
          </div>
        ))}
      </div>

      <ChartNote methodology={methodology} />

      {summary && <p className="mt-4 text-[13.5px] text-slate-600 leading-relaxed">{summary}</p>}

      {/* Accessible data table (chart data as crawlable HTML text) */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left border-collapse text-[12.5px]">
          <caption className="sr-only">{title || tableLabel}</caption>
          <thead>
            <tr className="border-b border-slate-200">
              <th scope="col" className="py-2 pr-3 font-bold text-slate-500">{tableLabel}</th>
              {categories.map((c, i) => (
                <th key={i} scope="col" className="py-2 px-3 font-bold text-slate-700">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {series.map((s, si) => (
              <tr key={si} className="border-b border-slate-100">
                <th scope="row" className="py-2 pr-3 font-semibold text-slate-700">{s.label}</th>
                {s.values.map((v, vi) => (
                  <td key={vi} className="py-2 px-3 text-slate-600">{fmt(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
};

export default ScienceChart;
